/*
 * card-color-swap.js — in-place color switching for product cards.
 *
 * Clicking a color_group swatch on a card (collection grid, homepage
 * collection experience, search, recently viewed, etc.) swaps the whole
 * .product-card-wrapper for the sibling product's card instead of navigating
 * to its PDP. The sibling card is fetched via the Section Rendering API
 * endpoint sections/card-color-swap.liquid
 * (/products/<handle>?section_id=card-color-swap) and cached, so the
 * swapped-in markup is the exact card-product.liquid render for that
 * product — images, price, badges, title links and quick-add all point at
 * the selected color. The card image slider rebinds itself (its
 * MutationObserver watches for added/removed sliders) and quick-add,
 * wishlist and slider arrows are all document-delegated, so a swapped card
 * needs no manual re-init.
 *
 * Modified clicks (cmd/ctrl/shift/alt) keep native link behavior, and any
 * fetch failure falls back to navigating to the swatch href.
 */
(() => {
  if (window.__cardColorSwapInitialized) return;
  window.__cardColorSwapInitialized = true;

  const SECTION_ID = 'card-color-swap';
  const SWATCH_SELECTOR = '.card__swatches .swatch-link[data-card-swap]';
  const htmlCache = new Map(); // product url -> Promise<string>
  const warmedImages = new Set();
  let swapInstance = 0;

  // Every swapped-in card renders with section_id 'card-swap', so two swaps of
  // the same product (e.g. mega-menu + grid) would collide on every element id
  // — and quick-add resolves its submit button and modal via document-wide id
  // lookups, so a collision opens the other (possibly hidden) card's modal.
  // Suffix all ids and the attributes that reference them with a unique token.
  const dedupeIds = (card) => {
    const uid = `cswap${++swapInstance}`;
    card.querySelectorAll('[id]').forEach((el) => {
      el.id = `${el.id}-${uid}`;
    });
    card.querySelectorAll('[for]').forEach((el) => {
      el.setAttribute('for', `${el.getAttribute('for')}-${uid}`);
    });
    card.querySelectorAll('[data-quick-add-target]').forEach((el) => {
      el.setAttribute('data-quick-add-target', `${el.getAttribute('data-quick-add-target')}-${uid}`);
    });
    card.querySelectorAll('[data-modal]').forEach((el) => {
      el.setAttribute('data-modal', `${el.getAttribute('data-modal')}-${uid}`);
    });
    ['aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((attr) => {
      card.querySelectorAll(`[${attr}]`).forEach((el) => {
        const refs = el
          .getAttribute(attr)
          .split(/\s+/)
          .filter(Boolean)
          .map((ref) => `${ref}-${uid}`)
          .join(' ');
        el.setAttribute(attr, refs);
      });
    });
  };

  const FETCH_TIMEOUT_MS = 6000;

  // Facets-rendered grids append per-render analytics params to swatch hrefs
  // (?_pos=…&_fid=…&_ss=c); strip them so cache keys stay stable and the
  // section request is clean. Fallback navigation keeps the original href.
  const productPath = (url) => url.split('?')[0];

  const fetchCardHTML = (rawUrl) => {
    const url = productPath(rawUrl);
    let pending = htmlCache.get(url);
    if (!pending) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      pending = fetch(`${url}?section_id=${SECTION_ID}`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          htmlCache.delete(url); // allow a retry on the next interaction
          throw error;
        })
        .finally(() => clearTimeout(timer));
      htmlCache.set(url, pending);
    }
    return pending;
  };

  const buildCard = (html, referenceCard) => {
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const card = holder.querySelector('.product-card-wrapper');
    if (!card) return null;
    dedupeIds(card);

    // Graft context only the hosting grid knows: its responsive sizes and its
    // fixed card ratio, so the swapped card keeps the exact footprint and
    // image selection of the card it replaces.
    const referenceImg = referenceCard.querySelector('.card-image-slider__img');
    const sizes = referenceImg && referenceImg.getAttribute('sizes');
    if (sizes) {
      card.querySelectorAll('.card-image-slider__img').forEach((img) => img.setAttribute('sizes', sizes));
    }
    ['.card', '.card__inner'].forEach((selector) => {
      const from = referenceCard.querySelector(selector);
      const to = card.querySelector(selector);
      const ratio = from && from.style.getPropertyValue('--ratio-percent');
      if (ratio && to) to.style.setProperty('--ratio-percent', ratio);
    });
    return card;
  };

  // Move the selected ring (is-selected + aria-current) to the swatch whose
  // product matches selectedPath. Hrefs may carry analytics params on
  // facets-rendered grids, so compare normalized paths.
  const markSelected = (scope, selectedPath) => {
    scope.querySelectorAll(SWATCH_SELECTOR).forEach((link) => {
      const isSelected = productPath(link.getAttribute('href') || '') === selectedPath;
      link.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  // Each product's color_group metafield lists ITSELF first, so the fetched
  // sibling card renders its swatch row in a different order — the clicked
  // color would jump to position 1 and the ring would appear stuck on the
  // first swatch. Keep the row the shopper is already looking at (clone it
  // into the fresh card) and just move the ring.
  const graftSwatchRow = (fresh, referenceCard, selectedPath) => {
    const freshRow = fresh.querySelector('.card__swatches');
    const referenceRow = referenceCard.querySelector('.card__swatches');
    if (!freshRow || !referenceRow) return;
    const row = referenceRow.cloneNode(true);
    freshRow.replaceWith(row);
    markSelected(fresh, selectedPath);
  };

  // Start the first slide's image download ahead of the click. Lazy images in
  // a detached tree never load, so flip the parsed card's first image to eager.
  const warmFirstImage = (url, html, referenceCard) => {
    if (warmedImages.has(url)) return;
    warmedImages.add(url);
    const card = buildCard(html, referenceCard);
    const img = card && card.querySelector('.card-image-slider__img');
    if (img) img.loading = 'eager';
  };

  const prefetch = (event) => {
    const link = event.target.closest(SWATCH_SELECTOR);
    if (!link || link.classList.contains('is-selected')) return;
    const card = link.closest('.product-card-wrapper');
    if (!card || !link.getAttribute('href')) return;
    const url = productPath(link.getAttribute('href'));
    fetchCardHTML(url)
      .then((html) => warmFirstImage(url, html, card))
      .catch(() => {});
  };

  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('touchstart', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);

  document.addEventListener('click', (event) => {
    const link = event.target.closest(SWATCH_SELECTOR);
    if (!link) return;
    // Keep native behavior for new-tab/window clicks.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (link.classList.contains('is-selected')) return;

    const card = link.closest('.product-card-wrapper');
    const url = link.getAttribute('href');
    if (!card || !url) {
      if (url) window.location.href = url;
      return;
    }

    card.__cardSwapUrl = url; // last click wins when several are in flight
    card.classList.add('card-swapping');
    markSelected(card, productPath(url)); // instant feedback while the card loads

    fetchCardHTML(url)
      .then(async (html) => {
        if (!card.isConnected || card.__cardSwapUrl !== url) return;
        const fresh = buildCard(html, card);
        if (!fresh) throw new Error('No card in section response');
        graftSwatchRow(fresh, card, productPath(url));

        // Decode the first image before swapping so the card never goes blank.
        const firstImg = fresh.querySelector('.card-image-slider__img');
        if (firstImg) {
          firstImg.loading = 'eager';
          if (firstImg.decode) {
            await Promise.race([
              firstImg.decode().catch(() => {}),
              new Promise((resolve) => setTimeout(resolve, 1200)),
            ]);
          }
        }
        if (!card.isConnected || card.__cardSwapUrl !== url) return;

        // ModalDialog self-moves quick-add modals to <body> on connect, so the
        // outgoing card's modal would otherwise be orphaned there on each swap.
        const opener = card.querySelector('modal-opener[data-modal]');
        const modalSelector = opener && opener.getAttribute('data-modal');
        if (modalSelector) {
          let staleModal = null;
          try {
            staleModal = document.querySelector(modalSelector);
          } catch (e) {
            /* invalid selector — nothing to clean up */
          }
          if (staleModal && !card.contains(staleModal) && !staleModal.hasAttribute('open')) {
            staleModal.remove();
          }
        }

        const hadFocus = card.contains(document.activeElement);
        card.replaceWith(fresh);
        if (hadFocus) {
          const selected = fresh.querySelector('.card__swatches .swatch-link.is-selected');
          if (selected) selected.focus({ preventScroll: true });
        }
      })
      .catch(() => {
        // Network/render failure: fall back to the swatch's normal navigation.
        window.location.href = url;
      });
  });
})();
